import React, { useMemo, useEffect, useState, useCallback } from "react";
import "./summary-component.css";
import EmailModal from "./email-modal";
import hommeBarbe from "@assets/svg/normal/homme-barbes.svg";
import hommeCheveux from "@assets/svg/normal/homme-coupe.svg";
import hommeCheveuxEtBarbe from "@assets/svg/normal/homme-barbel-cheveux.svg";
import NavigationButton from "./navigation-button";
import { useConfig } from "../../contexts";
import { getSvgPaths } from "../../utils/svgAssets";
import {
  resolveNormalPrice,
  resolveSubscriberPrice,
  isHommePath as checkIsHommePath,
  isDebugPricingEnabled,
  getFrequency,
  getBrushingNormalPrice,
} from "../../utils/priceUtils";

const SummaryComponent = ({ selections, onSendEmail, subscriberTotal }) => {
  const { config: configData } = useConfig();
  const isDebugPricing = isDebugPricingEnabled();
  const isHommePath = useMemo(() => checkIsHommePath(selections), [selections]);

  const hommePathType = useMemo(() => {
    const hasBarbe = Object.keys(selections).some(
      (key) => key === "h-b-1" || key === "h-b-c-2"
    );
    const hasCheveux = Object.keys(selections).some(
      (key) => key === "h-c-1" || key === "h-b-c-1"
    );

    let type = null;
    if (hasBarbe && !hasCheveux) type = "barbe-only";
    else if (!hasBarbe && hasCheveux) type = "cheveux-only";
    else if (hasBarbe && hasCheveux) type = "barbe-cheveux";
    return type;
  }, [selections]);

  const getMainSelection = () => {
    const initialStep = Object.keys(selections).find((key) => key === "f-0");
    if (initialStep && selections[initialStep]) {
      return selections[initialStep].choice;
    }

    const hommeBarbe = Object.keys(selections).find((key) => key === "h-b-1");
    if (hommeBarbe && selections[hommeBarbe]) {
      return { ...selections[hommeBarbe].choice, isHommeBarbe: true };
    }

    const hommeCheveux = Object.keys(selections).find((key) => key === "h-c-1");
    if (hommeCheveux && selections[hommeCheveux]) {
      return { ...selections[hommeCheveux].choice, isHommeCheveux: true };
    }

    const hommeChevBarbe = Object.keys(selections).find(
      (key) => key === "h-b-c-1"
    );
    if (hommeChevBarbe && selections[hommeChevBarbe]) {
      return { ...selections[hommeChevBarbe].choice, isHommeChevBarbe: true };
    }

    const hommeChevBarbe2 = Object.keys(selections).find(
      (key) => key === "h-b-c-2"
    );
    if (hommeChevBarbe2 && selections[hommeChevBarbe2]) {
      return { ...selections[hommeChevBarbe2].choice, isHommeBarbe: true };
    }

    return null;
  };

  const mainSelection = getMainSelection();
  const mainType = mainSelection?.label || "";


  const collectSelectionDetails = useMemo(() => {
    const details = {
      mainService: { type: "", label: "", price: 0, frequency: 1 },
      coupe: { selected: false, price: 0 },
      keratine: { selected: false, price: 0, frequency: 1 },
      // brushing.price inclut désormais longueur + épaisseur via la matrice
      brushing: { selected: false, count: 0, price: 0 },
      premium: { selected: false, price: 0 },
      // lengthThickness : conservé pour l'affichage, plus pour le calcul de prix
      lengthThickness: {
        length: { label: "", type: "" },
        thickness: { label: "", type: "" },
      },
    };

    Object.entries(selections).forEach(([step, selection]) => {
      if (step === "f-0" && selection && selection.choice) {
        const choice = selection.choice;
        details.mainService.type = choice.label.toLowerCase();
        details.mainService.label = choice.label;
        // Prix résolu via la matrice (longueur/épaisseur baked-in)
        details.mainService.price = resolveSubscriberPrice(choice, selections);

        if (choice.selectedValue) {
          details.mainService.frequency = getFrequency(choice);
        }

        if (choice.label.toLowerCase() === "brushing") {
          details.brushing.selected = true;
        }
      }

      // Longueur / épaisseur : on conserve les labels pour l'affichage uniquement
      if (step === "f-c-1" || step === "f-g-1" || step === "f-b-1") {
        if (selection && typeof selection === "object" && !selection.choice) {
          Object.entries(selection).forEach(([, groupSelection]) => {
            if (groupSelection && groupSelection.choice) {
              const choice = groupSelection.choice;
              if (choice.group && choice.group.toLowerCase() === "longeur") {
                details.lengthThickness.length.label = choice.label || "";
                details.lengthThickness.length.type = choice.label.toLowerCase();
              } else if (choice.group && choice.group.toLowerCase() === "epaisseur") {
                details.lengthThickness.thickness.label = choice.label || "";
                details.lengthThickness.thickness.type = choice.label.toLowerCase();
              }
            }
          });
        } else if (selection && selection.choice) {
          const choice = selection.choice;
          if (choice.group && choice.group.toLowerCase() === "longeur") {
            details.lengthThickness.length.label = choice.label || "";
          } else if (choice.group && choice.group.toLowerCase() === "epaisseur") {
            details.lengthThickness.thickness.label = choice.label || "";
          }
        }
      }

      if (step === "f-c-2" && selection && selection.choice) {
        const choice = selection.choice;
        if (choice.label.toLowerCase().includes("premium")) {
          details.premium.selected = true;
          details.premium.price = resolveSubscriberPrice(choice, selections);
        }
      }

      if (
        selection &&
        selection.choice &&
        selection.choice.label &&
        selection.choice.label.toLowerCase().includes("ajouter une coupe") &&
        !selection.choice.label.toLowerCase().includes("ne souhaite pas") &&
        !step.startsWith("modal-")
      ) {
        details.coupe.selected = true;
        details.coupe.price = Number(selection.choice["subscriber-price"]) || 0;
      }

      if (
        selection &&
        selection.choice &&
        selection.choice.label &&
        selection.choice.label.toLowerCase().includes("kératine") &&
        !selection.choice.label.toLowerCase().includes("ne souhaite pas")
      ) {
        details.keratine.selected = true;
        details.keratine.price = Number(selection.choice["subscriber-price"]) || 0;

        if (selection.choice.selectedValue) {
          details.keratine.frequency = getFrequency(selection.choice);
        }
      }

      if (
        step.startsWith("modal-") &&
        selection &&
        selection.choice &&
        selection.choice.selectedValue
      ) {
        details.brushing.selected = true;
        details.brushing.count = parseInt(selection.choice.selectedValue, 10) || 0;
        // Prix résolu via la matrice (longueur/épaisseur baked-in)
        details.brushing.price = resolveSubscriberPrice(selection.choice, selections);
      }
    });
    return details;
  }, [selections]);

  const calculateTotalCost = (details) => {
    if (isDebugPricing) {
      //console.group("[Addict][Summary][ABONNÉ] calculateTotalCost");
      //console.log("Détails (subscriber):", details);
      //console.log("Sélections (subscriber):", selections);
    }
    let total = 0;

    if (isHommePath) {
      Object.entries(selections).forEach(([, selection]) => {
        if (selection && selection.choice) {
          const choice = selection.choice;
          let itemPrice = Number(choice["subscriber-price"]) || 0;

          if (choice.selectedValue) {
            itemPrice *= getFrequency(choice);
          }

          total += itemPrice;

          if (isDebugPricing) {
            /*
            console.log(
              `[Homme][TotalCost] ${step} – ${choice.label} (freq=${
                choice.selectedValue || 1
              }) → +${itemPrice}, total intermédiaire : ${total}`
            );
            */
          }
        }
      });

      if (isDebugPricing) {
        //console.log("[Homme][TotalCost ABONNÉ] Total final (subscriber):", total);
        //console.groupEnd();
      }
      return total > 0 ? total.toFixed(2) : "0.00";
    }

    // Prix du service principal × fréquence (longueur/épaisseur inclus via matrice)
    let serviceBasePrice = details.mainService.price;

    if (details.mainService.type === "coloration" && details.premium.selected) {
      serviceBasePrice = details.premium.price; // le prix premium remplace le prix classique (f-0)
    }

    total += serviceBasePrice * details.mainService.frequency;

    if (details.coupe.selected) {
      total += details.coupe.price;
    }

    if (details.keratine.selected) {
      total += details.keratine.price * details.keratine.frequency;
    }

    // Brushings supplémentaires (coloration / gloss) — prix via matrice
    if (
      details.brushing.selected &&
      details.brushing.count > 0 &&
      details.mainService.type !== "brushing"
    ) {
      total += details.brushing.price * details.brushing.count;
    }
    if (isDebugPricing) {
      console.log("[Summary][TotalCost] Total final:", total);
      console.groupEnd();
    }
    return total > 0 ? total.toFixed(2) : "0.00";
  };
  /*
  const getUnitaryPricesFromConfig = () => {
    const unitaryPrices = {};
    
    try {
      configData.steps.forEach(step => {
        if (step.choices) {
          step.choices.forEach(choice => {
            if (choice['normal-price'] !== undefined) {
              const key = choice.group 
                ? `${choice.label.toLowerCase()}_${choice.group.toLowerCase()}`
                : choice.label.toLowerCase();
              
              unitaryPrices[key] = Number(choice['normal-price']) || 0;
            }
          });
        }
        
        if (step.modal && step.modal.choices) {
          step.modal.choices.forEach(choice => {
            if (choice['normal-price'] !== undefined) {
              const key = choice.group 
                ? `modal_${choice.group.toLowerCase()}`
                : 'modal_brushing_supplementaire';
              
              unitaryPrices[key] = Number(choice['normal-price']) || 0;
            }
          });
        }
      });
    } catch (error) {
      console.error("❌ Erreur lors de l'extraction des prix unitaires:", error);
    }
    
    return unitaryPrices;
  };
  */

  const findUnitaryPrice = (stepName, choiceLabel, choiceGroup = null) => {
    try {
      const step = configData.steps.find((s) => s.name === stepName);
      if (!step) return 0;

      if (step.choices) {
        const choice = step.choices.find(
          (c) =>
            c.label === choiceLabel && (!choiceGroup || c.group === choiceGroup)
        );
        if (choice && choice["normal-price"] !== undefined) {
          return Number(choice["normal-price"]) || 0;
        }
      }

      if (step.modal && step.modal.choices) {
        const choice = step.modal.choices.find(
          (c) => !choiceGroup || c.group === choiceGroup
        );
        if (choice && choice["normal-price"] !== undefined) {
          return Number(choice["normal-price"]) || 0;
        }
      }

      return 0;
    } catch (error) {
      console.error(
        `❌ Erreur lors de la recherche du prix unitaire pour ${stepName}:`,
        error
      );
      return 0;
    }
  };

  const calculateSavings = (details, totalCost) => {
/*
    if (isDebugPricing) {
      console.group("[Addict][Summary][NORMAL] calculateSavings");
      console.log("Total abonnement (mensuel, subscriber):", totalCost);
    }*/

    let totalStandardPrice = 0;
    const monthlySubscriptionPrice = parseFloat(totalCost);

    if (isHommePath) {
      Object.entries(selections).forEach(([step, selection]) => {
        if (selection && selection.choice) {
          const choice = selection.choice;
          const frequency = getFrequency(choice);

          const unitaryPrice = findUnitaryPrice(step, choice.label);
          const totalPrice = unitaryPrice * frequency;
          totalStandardPrice += totalPrice;
        }
      });
    } else {
      // Si on est une femme
      /*
      if (isDebugPricing) {
        console.log("Femme – Length:", details.lengthThickness.length.label);
        console.log(
          "Femme – Thickness:",
          details.lengthThickness.thickness.label
        );
        
      }
      */

      if (details.mainService.type) {
        //console.log("==> Main Service Type : " + details.mainService.type);

        const mainServiceStep = Object.keys(selections).find(
          (key) => key === "f-0"
        );
        if (mainServiceStep && selections[mainServiceStep]?.choice) {
          let unitaryPrice = 0;

          const frequency = details.mainService.frequency || 1;

          // TARIFS DES PRESTATIONS PRINCIPALES A LA CARTE (non abonné)
          // Résolution via normal-price-matrix si disponible dans la config
          const f0Step = configData.steps.find((s) => s.name === "f-0");
          const f0Choice = f0Step?.choices?.find(
            (c) => c.label === details.mainService.label
          );

          if (f0Choice) {
            unitaryPrice = resolveNormalPrice(f0Choice, selections);
          }

          // Prix Premium (coloration uniquement) — remplace le prix classique de f-0
          if (details.mainService.type === "coloration" && details.premium.selected) {
            const fcStep = configData.steps.find((s) => s.name === "f-c-2");
            const premiumChoice = fcStep?.choices?.find((c) => c.label === "Premium");
            if (premiumChoice) {
              unitaryPrice = resolveNormalPrice(premiumChoice, selections);
            }
          }

          totalStandardPrice += unitaryPrice * frequency;

          /*
          Pour coloration/gloss : chaque session à la carte inclut un brushing
          (service de base + shampoing + brushing). On ajoute donc le prix brushing
          × fréquence. Si une coupe est prise en option, on l'ajoute une seule fois.
          Pour le parcours brushing, le prix de la matrice f-0 couvre déjà
          shampoing + brushing — rien à ajouter ici.
          */
          // Si le choix principal déclare "add-normal-brushing-price",
          // on ajoute le prix brushing (f-0 / Brushing) résolu via sa matrice
          if (f0Choice?.["add-normal-brushing-price"]) {
            totalStandardPrice += getBrushingNormalPrice(selections, configData.steps) * frequency;
          }

          // Coupe en option — utilise le step correspondant au service sélectionné
          if (details.coupe.selected) {
            const coupeStepByType = {
              brushing:   "f-b-2",
              coloration: "f-c-3",
              gloss:      "f-g-2",
            };
            const coupeStepName = coupeStepByType[details.mainService.type];
            if (coupeStepName) {
              const step = configData.steps.find((s) => s.name === coupeStepName);
              const coupeChoice = step?.choices?.find(
                (c) => c.label === "Oui, ajouter une coupe"
              );
              if (coupeChoice) {
                totalStandardPrice += Number(coupeChoice["normal-price"]) || 0;
              }
            }
          }
        }
      }

      // Kératine - vérifier que l'utilisateur veut vraiment la kératine
      if (details.keratine.selected && details.keratine.price > 0) {
        const keratineSteps = ["f-c-4", "f-b-3", "f-g-3"];
        for (const stepName of keratineSteps) {
          const selection = selections[stepName];
          if (
            selection?.choice?.label?.toLowerCase().includes("kératine") &&
            !selection.choice.label.toLowerCase().includes("ne souhaite pas")
          ) {
            const unitaryPrice = findUnitaryPrice(
              stepName,
              "Oui, ajouter un ou plusieurs lissage Kératine"
            );
            const frequency = details.keratine.frequency || 1;
            const totalPrice = unitaryPrice * frequency;
            totalStandardPrice += totalPrice;


            break;
          }
        }
      }

      // Brushings supplémentaires (coloration / gloss uniquement)
      if (
        details.brushing.selected &&
        details.brushing.count > 0 &&
        (details.mainService.type === "coloration" ||
          details.mainService.type === "gloss")
      ) {
        const modalSteps = ["f-c-4", "f-g-3"];
        for (const stepName of modalSteps) {
          const step = configData.steps.find((s) => s.name === stepName);
          if (step?.modal?.choices) {
            const brushingChoice = step.modal.choices.find(
              (c) => c["subscriber-price"] > 0
            );
            if (brushingChoice) {
              // Résolution via normal-price-matrix (longueur/épaisseur du parcours correct)
              const brushingUnitaryPrice = resolveNormalPrice(brushingChoice, selections);
              const totalPrice = brushingUnitaryPrice * details.brushing.count;
              totalStandardPrice += totalPrice;

              if (isDebugPricing) {
                console.log(
                  "[Brushing supplémentaire][Non abonné] count=",
                  details.brushing.count,
                  "prix unitaire=",
                  brushingUnitaryPrice,
                  "total=",
                  totalPrice
                );
              }

              break;
            }
          }
        }
      }
    }
    const savings = totalStandardPrice - monthlySubscriptionPrice;
    const finalSavings = Math.max(savings, 0);
    if (isDebugPricing) {
      //console.log("Total non abonné:", totalStandardPrice);
      //console.log("Économie finale:", finalSavings);
      //console.group("[Addict][Summary] Synthèse prix (abonné vs normal)");
      /*
      console.table([
        {
          type: "abonné",
          total: Number.isFinite(monthlySubscriptionPrice)
            ? monthlySubscriptionPrice.toFixed(2)
            : "0.00",
        },
        {
          type: "normal",
          total: Number.isFinite(totalStandardPrice)
            ? totalStandardPrice.toFixed(2)
            : "0.00",
        },
        {
          type: "économie",
          total: finalSavings.toFixed(2),
        },
      ]);
      console.groupEnd();
      console.groupEnd();
      */

    }
    return finalSavings.toFixed(2);
  };

  const getMainIcon = useCallback(() => {
    if (isHommePath) {
      if (hommePathType === "barbe-only") {
        return hommeBarbe;
      } else if (hommePathType === "cheveux-only") {
        return hommeCheveux;
      } else if (hommePathType === "barbe-cheveux") {
        return hommeCheveuxEtBarbe;
      }
    }

    if (!mainSelection) {
      const { normal } = getSvgPaths("femme-brushing");
      return normal;
    }

    if (
      mainSelection.img === "homme-barbes" ||
      mainSelection.img === "homme-protocole" ||
      mainSelection.img === "homme-barbes-entretien" ||
      mainSelection.isHommeBarbe
    ) {
      return hommeBarbe;
    }

    if (
      mainSelection.img === "homme-coupe" ||
      mainSelection.img === "homme-degrade" ||
      mainSelection.isHommeCheveux
    ) {
      return hommeCheveux;
    }

    if (
      mainSelection.img === "homme-barbel-cheveux" ||
      mainSelection.isHommeChevBarbe
    ) {
      return hommeCheveuxEtBarbe;
    }

    switch (mainSelection.label?.toLowerCase()) {
      case "coloration": {
        const colorationPaths = getSvgPaths("femme-coloration");
        return colorationPaths.normal;
      }
      case "gloss": {
        const glossPaths = getSvgPaths("femme-gloss");
        return glossPaths.normal;
      }
      case "brushing": {
        const brushingPaths = getSvgPaths("femme-brushing");
        return brushingPaths.normal;
      }
      default:
        return getSvgPaths("femme-brushing").normal;
    }
  }, [isHommePath, hommePathType, mainSelection]);

  const [mainIcon, setMainIcon] = useState(() => getMainIcon());
  const details = collectSelectionDetails;
  // subscriberTotal vient du wizard-container (déjà calculé) — évite un double calcul
  const totalCost = subscriberTotal ?? calculateTotalCost(details);
  const savings = calculateSavings(details, totalCost); // économie vs normal

  if (isDebugPricing) {
    const totalSubscriber = parseFloat(totalCost || "0") || 0;
    const totalNormal = totalSubscriber + parseFloat(savings || "0") || 0;
    console.group("[Addict][Summary] Vue globale prix");
    console.log("Total abonné (subscriber):", totalSubscriber);
    console.log("Total normal (hors abo):", totalNormal);
    console.log("Économie réalisée:", savings);
    console.groupEnd();
  }

  useEffect(() => {
    const updateMainIcon = () => {
      setMainIcon(getMainIcon());
    };

    const handleSelectionChange = (event) => {
      if (
        event.detail &&
        event.detail.choice &&
        event.detail.choice.group === "Longeur"
      ) {
        setTimeout(updateMainIcon, 50);
      }
    };

    document.addEventListener("selection-changed", handleSelectionChange);
    document.addEventListener("hair-length-icons-refresh", updateMainIcon);

    return () => {
      document.removeEventListener("selection-changed", handleSelectionChange);
      document.removeEventListener("hair-length-icons-refresh", updateMainIcon);
    };
  }, [getMainIcon]);

  const getSummaryText = () => {
    let baseText = "";
    const frequency = details.mainService.frequency || 1;

    if (isHommePath) {
      const hasBarbe = Object.keys(selections).some(
        (key) => key === "h-b-1" || key === "h-b-c-2"
      );
      const hasCheveux = Object.keys(selections).some(
        (key) => key === "h-c-1" || key === "h-b-c-1"
      );

      if (hasCheveux && hasBarbe) {
        let chevFrequency = frequency;
        let barbeFrequency = frequency;

        Object.entries(selections).forEach(([step, selection]) => {
          if ((step === "h-b-c-1" || step === "h-c-1") && selection.choice?.selectedValue) {
            chevFrequency = getFrequency(selection.choice);
          }
          if ((step === "h-b-c-2" || step === "h-b-1") && selection.choice?.selectedValue) {
            barbeFrequency = getFrequency(selection.choice);
          }
        });

        return `Vos cheveux disciplinés <span>${chevFrequency} fois</span> / mois<br>et votre barbe sera soignée <span>${barbeFrequency} fois</span> / mois`;
      } else if (hasCheveux) {
        let chevFrequency = 1;
        Object.entries(selections).forEach(([step, selection]) => {
          if (step === "h-c-1" && selection.choice?.selectedValue) {
            chevFrequency = getFrequency(selection.choice);
          }
        });
        return `Vos cheveux disciplinés <span>${chevFrequency} fois</span> / mois`;
      } else if (hasBarbe) {
        let barbeFrequency = 1;
        Object.entries(selections).forEach(([step, selection]) => {
          if (step === "h-b-1" && selection.choice?.selectedValue) {
            barbeFrequency = getFrequency(selection.choice);
          }
        });
        return `Votre barbe sera soignée <span>${barbeFrequency} fois</span> / mois`;
      }
    }

    switch (details.mainService.type) {
      case "coloration":
        baseText = `Votre couleur sera entretenue <span>${frequency} fois</span> / mois`;
        break;
      case "gloss":
        baseText = `Votre gloss sera entretenu <span>${frequency} fois</span> / mois`;
        break;
      case "brushing":
        baseText = `Votre brushing sera effectué <span>${frequency} fois</span> / mois`;
        break;
      default:
        baseText = `Votre abonnement sera effectué <span>${frequency} fois</span> / mois`;
    }

    let additionalText = "";

    if (details.coupe.selected && details.coupe.price > 0) {
      additionalText += " avec une coupe";
    }

    if (details.keratine.selected) {
      if (additionalText) {
        additionalText += ` et <span>${
          details.keratine.frequency > 1 ? details.keratine.frequency : "1"
        }</span> lissage${details.keratine.frequency > 1 ? "s" : ""} Kératine`;
      } else {
        additionalText += ` avec <span>${
          details.keratine.frequency > 1 ? details.keratine.frequency : "1"
        }</span> lissage${details.keratine.frequency > 1 ? "s" : ""} Kératine`;
      }
    }

    if (
      details.brushing.selected &&
      details.brushing.count > 0 &&
      details.mainService.type !== "brushing"
    ) {
      if (additionalText) {
        additionalText += ` + <span>${details.brushing.count}</span> brushing${
          details.brushing.count > 1 ? "s" : ""
        }`;
      } else {
        additionalText += ` avec <span>${
          details.brushing.count
        }</span> brushing${details.brushing.count > 1 ? "s" : ""}`;
      }
    }

    if (additionalText) {
      return `${baseText}${additionalText} / mois`;
    }

    return baseText;
  };

  const getPriceDetails = () => {
    const priceItems = [];

    if (isHommePath) {
      Object.entries(selections).forEach(([step, selection]) => {
        if (selection && selection.choice) {
          const choice = selection.choice;

          if (
            (step === "h-c-1" || step === "h-b-c-1") &&
            choice.label &&
            Number(choice["subscriber-price"]) > 0
          ) {
            let label = choice.label.toLowerCase().includes("dégradé")
              ? "le dégradé américain"
              : "la coupe classique";
            const frequency = getFrequency(choice);

            if (frequency > 1) {
              label += ` (x${frequency})`;
            }

            priceItems.push({
              label,
              price: choice["subscriber-price"].toFixed(2),
            });
          }

          if (
            (step === "h-b-1" || step === "h-b-c-2") &&
            choice.label &&
            Number(choice["subscriber-price"]) > 0
          ) {
            let label = choice.label.toLowerCase().includes("protocole")
              ? "le protocole barbe"
              : "l'entretien barbe courte";
            const frequency = getFrequency(choice);

            if (frequency > 1) {
              label += ` (x${frequency})`;
            }

            priceItems.push({
              label,
              price: choice["subscriber-price"].toFixed(2),
            });
          }
        }
      });

      return priceItems;
    }

    if (details.mainService.type) {
      let label = "";
      let price = details.mainService.price;
      const frequency = details.mainService.frequency || 1;

      if (
        details.mainService.type === "coloration" &&
        details.premium.selected
      ) {
        price = details.premium.price; // le prix premium remplace le prix classique (f-0)
      }

      switch (details.mainService.type) {
        case "coloration":
          label = "le forfait coloration (couleur, shampoing, brushing)";
          break;
        case "gloss":
          label = "le gloss";
          break;
        case "brushing":
          label = "le brushing";
          break;
        default:
          label = "le service principal";
      }

      if (frequency > 1) {
        label = `${label} (x${frequency})`;
      }

      priceItems.push({
        label,
        price: price.toFixed(2),
      });
    }

    if (details.coupe.selected && parseFloat(details.coupe.price) > 0) {
      priceItems.push({
        label: "la coupe",
        price: parseFloat(details.coupe.price).toFixed(2),
      });
    }

    if (details.keratine.selected && parseFloat(details.keratine.price) > 0) {
      const frequency = details.keratine.frequency || 1;
      let label = "le lissage kératine";
      let price = details.keratine.price;

      if (frequency > 1) {
        label = `${label} (x${frequency})`;
      }

      priceItems.push({
        label,
        price: price.toFixed(2),
      });
    }

    if (
      details.brushing.selected &&
      details.brushing.count > 0 &&
      (details.mainService.type === "coloration" ||
        details.mainService.type === "gloss")
    ) {
      const count = details.brushing.count;

      priceItems.push({
        label: `le brushing supplémentaire${count > 1 ? ` (x${count})` : ""}`,
        price: details.brushing.price.toFixed(2),
      });
    }
    return priceItems;
  };

  const summaryText = getSummaryText();
  const priceDetails = getPriceDetails();

  const createSummaryData = () => {
    const mainSelection = getMainSelection();
    const mainType = mainSelection?.label || "";
    const details = collectSelectionDetails;
    const totalCost = calculateTotalCost(details);
    const savings = calculateSavings(details, totalCost);
    const summaryText = getSummaryText();
    const priceDetails = getPriceDetails();

    const frequency = mainSelection ? getFrequency(mainSelection) : 1;

    const hasCut = details.coupe && details.coupe.selected;
    const hasKeratine = details.keratine && details.keratine.selected;
    const brushingCount =
      details.brushing && details.brushing.count ? details.brushing.count : 0;

    return {
      mainType,
      totalCost,
      savings,
      summaryText,
      priceDetails,
      isHomme: isHommePath,
      serviceType: mainType,
      frequency,
      hasCut,
      hasKeratine,
      brushingCount,
      details,
    };
  };

  useEffect(() => {
    const summaryData = createSummaryData();
    localStorage.setItem("addict_summary_data", JSON.stringify(summaryData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  return (
    <div className="summary-container">
      <div className="summary-icon-container">
        <img src={mainIcon} alt={mainType} className="summary-icon" />
      </div>
      <div className="summary-reduction">
        <p className="summary-gold-title-economie">
          Vous économisez <span className="gold">{savings}€</span>.
        </p>
      </div>
      <div className="summary-gold-box">
        <p className="summary-gold-title">
          Avec votre Abo Addict à{" "} 
          <span className="gold-price-highlight">{totalCost}€</span> / mois<sup>*</sup>
        </p>
        <p className="summary-gold-subtitle">votre look est toujours au top.</p>
        <p className="summary-gold-saving">
          <br />À la carte vous auriez payé{" "}
          <span className="gold-price-highlight">
            {(parseFloat(totalCost) + parseFloat(savings)).toFixed(2)}€
          </span>
        </p>
        <p className="summary-gold-warning">
          * Tarifs indicatifs. Ils peuvent varier selon le salon et la nature des cheveux. Un devis précis sera réalisé en salon après diagnostic.
        </p>
      </div>

      <div className="summary-description">
        <p dangerouslySetInnerHTML={{ __html: summaryText }}></p>
      </div>

      <div className="summary-price-details">
        <p className="summary-price-title">
          soit un tarif unitaire des prestations abonnées de
        </p>

        {priceDetails.map((detail, index) => (
          <div className="summary-price-item" key={index}>
            <span className="summary-price-value">{detail.price}€</span>
            <span className="summary-price-label">{detail.label}</span>
          </div>
        ))}
      </div>

      <div className="summary-action">
        <NavigationButton
          isEnabled={true}
          onClick={onSendEmail}
          label="Recevoir ma simulation par email"
        />
      </div>
    </div>
  );
};

export default SummaryComponent;
